// Remove direct import, will use lazy loading when needed
// import * as pdfjsLib from 'pdfjs-dist';
import type { PDFPageProxy } from 'pdfjs-dist/types/src/display/api';

export const TARGET_DPI = 200;
export const BASE_PDF_DPI = 72;
export const DEFAULT_SCALE = TARGET_DPI / BASE_PDF_DPI; // ≈2.78

export function isPDFFile(file: File): boolean {
  return file.type === 'application/pdf';
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

export function getFileType(file: File): 'pdf' | 'image' | null {
  if (isPDFFile(file)) return 'pdf';
  if (isImageFile(file)) return 'image';
  return null;
}

export function findContentBounds(imageData: ImageData): { x: number; y: number; width: number; height: number } {
  const { data, width, height } = imageData;
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;

  // Scan the image data to find non-white pixels
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      
      // Check if pixel is not white (allowing for some tolerance)
      if (r < 250 || g < 250 || b < 250) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  // Add some padding
  const padding = 10;
  return {
    x: Math.max(0, minX - padding),
    y: Math.max(0, minY - padding),
    width: Math.min(width - minX + padding, maxX - minX + padding * 2),
    height: Math.min(height - minY + padding, maxY - minY + padding * 2)
  };
}

export async function cropCanvasToContent(canvas: HTMLCanvasElement): Promise<HTMLCanvasElement> {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const bounds = findContentBounds(imageData);

  // Create a new canvas with the cropped dimensions
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = bounds.width;
  croppedCanvas.height = bounds.height;
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) throw new Error('Failed to get cropped canvas context');

  // Copy the cropped region to the new canvas
  croppedCtx.drawImage(
    canvas,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height,
    0,
    0,
    bounds.width,
    bounds.height
  );

  return croppedCanvas;
}

export async function renderPDFPageToCanvas(
  page: PDFPageProxy,
  scale: number = DEFAULT_SCALE
): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  if (!context) {
    throw new Error('Failed to get canvas context');
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport: viewport,
    background: 'white'
  }).promise;

  return canvas;
}

export async function processPDFPage(
  page: PDFPageProxy,
  scale: number = DEFAULT_SCALE
): Promise<HTMLCanvasElement> {
  // First render the page to canvas
  const canvas = await renderPDFPageToCanvas(page, scale);
  
  // Then crop it to remove white margins
  return cropCanvasToContent(canvas);
} 